import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useFragments } from '@/context/FragmentsContext';
import { extractFragmentParamNames } from '@/helpers/fragmentParams.helpers';
import { toFragmentName } from '@/helpers/fragmentName.helpers';
import { getApiErrorMessage } from '@/api/base.api';
import { logger } from '@/utils/logger';

/** Identifier grammar the backend enforces for fragment names/ops. */
const FRAGMENT_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * Example steps, shown as the textarea placeholder.
 *
 * Uses the one-call macros (`fetch`, `aggregate`, `compare`, `profile`,
 * `distinct_values`) rather than the raw primitives, and relies on IMPLICIT
 * CHAINING: the second step omits `from`, so it consumes the first step's
 * output. Both are what the backend now recommends for a hand-written
 * fragment.
 */
const STEPS_PLACEHOLDER = [
  '[',
  '  { "op": "fetch",     "args": { "label": "Invoice", "limit": 20 } },',
  '  { "op": "aggregate", "args": { "by": ["status"] } }',
  ']',
].join('\n');

/** Result of parsing the steps textarea: a JSON array or an error string. */
type StepsParseResult =
  | { ok: true; steps: unknown[] }
  | { ok: false; error: string };

const parseSteps = (text: string): StepsParseResult => {
  if (text.trim().length === 0) {
    return { ok: false, error: 'Steps are required.' };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Invalid JSON.',
    };
  }
  if (!Array.isArray(parsed)) {
    return { ok: false, error: 'Steps must be a JSON array.' };
  }
  if (parsed.length === 0) {
    return { ok: false, error: 'Steps must contain at least one step.' };
  }
  return { ok: true, steps: parsed };
};

const FragmentEditorModal: React.FC = () => {
  const {
    isEditorModalOpen,
    setIsEditorModalOpen,
    currentEditingFragment,
    saveFragment,
    removeFragment,
  } = useFragments();

  const [name, setName] = useState('');
  const [doc, setDoc] = useState('');
  const [stepsText, setStepsText] = useState('');
  const [output, setOutput] = useState('');
  const [required, setRequired] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isEditing = currentEditingFragment !== null;

  // Populate form when the editing target changes / the modal opens
  useEffect(() => {
    if (currentEditingFragment) {
      setName(currentEditingFragment.name);
      setDoc(currentEditingFragment.doc);
      setStepsText(JSON.stringify(currentEditingFragment.steps, null, 2));
      setOutput(currentEditingFragment.output ?? '');
      setRequired(currentEditingFragment.required);
    } else {
      setName('');
      setDoc('');
      setStepsText('');
      setOutput('');
      setRequired([]);
    }
    setErrorMessage(null);
  }, [currentEditingFragment, isEditorModalOpen]);

  // Parse/validate on every keystroke so the error + param preview stay live
  // and the save button reflects validity without a separate validate click.
  const stepsParse = useMemo(() => parseSteps(stepsText), [stepsText]);

  // Only surface the parse error once the author has typed something, so an
  // untouched textarea doesn't show a red error on open (touched-gating).
  const stepsError =
    stepsText.trim().length > 0 && !stepsParse.ok ? stepsParse.error : null;

  // Params are derived from the `$param` refs inside steps (read-only).
  const paramNames = useMemo(
    () => (stepsParse.ok ? extractFragmentParamNames(stepsParse.steps) : []),
    [stepsParse]
  );

  const trimmedName = name.trim();
  const isNameValid = FRAGMENT_NAME_PATTERN.test(trimmedName);
  const nameSuggestion =
    trimmedName.length > 0 && !isNameValid
      ? toFragmentName(trimmedName)
      : null;
  const nameError =
    trimmedName.length > 0 && !isNameValid
      ? `Name must match ${FRAGMENT_NAME_PATTERN.source}` +
        (nameSuggestion ? ` — try "${nameSuggestion}"` : '')
      : null;

  const isFormValid = isNameValid && stepsParse.ok;

  const handleClose = () => {
    setIsEditorModalOpen(false);
    setErrorMessage(null);
  };

  const toggleRequired = (param: string) => {
    setRequired((prev) =>
      prev.includes(param)
        ? prev.filter((p) => p !== param)
        : [...prev, param]
    );
  };

  const handleSubmit = async () => {
    if (!isFormValid || !stepsParse.ok || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await saveFragment({
        name: trimmedName,
        steps: stepsParse.steps,
        params: paramNames,
        // Only keep required entries that still exist among the params.
        required: required.filter((p) => paramNames.includes(p)),
        output: output.trim() ? output.trim() : null,
        doc: doc.trim(),
      });
      handleClose();
    } catch (error) {
      logger.error('Failed to save fragment:', error);
      // Server deep-validates on define; surface its 400 detail verbatim.
      setErrorMessage(getApiErrorMessage(error, 'Failed to save fragment'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!currentEditingFragment) return;

    setIsDeleting(true);
    setErrorMessage(null);

    try {
      await removeFragment(currentEditingFragment.name);
      handleClose();
    } catch (error) {
      logger.error('Failed to delete fragment:', error);
      setErrorMessage(
        getApiErrorMessage(error, 'Failed to delete fragment')
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const dialogFooter = (
    <div className='flex items-center justify-between px-6 py-4'>
      <div>
        {isEditing && (
          <Button
            label='Delete'
            icon='pi pi-trash'
            onClick={() => void handleDelete()}
            className='!bg-red-600 !border-red-600 !text-white hover:!bg-red-700 hover:!border-red-700 disabled:!bg-red-400 disabled:!border-red-400'
            disabled={isSubmitting || isDeleting}
            loading={isDeleting}
            data-testid='fragment-editor-delete'
          />
        )}
      </div>
      <div className='flex items-center gap-3'>
        <Button
          label='Cancel'
          icon='pi pi-times'
          onClick={handleClose}
          className='p-button-text'
          disabled={isSubmitting || isDeleting}
        />
        <Button
          label={isEditing ? 'Update Fragment' : 'Create Fragment'}
          icon='pi pi-check'
          onClick={() => void handleSubmit()}
          disabled={!isFormValid || isSubmitting || isDeleting}
          loading={isSubmitting}
          data-testid='fragment-editor-save'
        />
      </div>
    </div>
  );

  return (
    <Dialog
      header={isEditing ? 'Edit Function' : 'Create Function'}
      visible={isEditorModalOpen}
      style={{ width: '70vw', height: '80vh' }}
      onHide={handleClose}
      draggable={false}
      footer={dialogFooter}
      maximizable
      contentClassName='!p-6 !m-0'
      className='!border-border'
    >
      <div className='h-full flex flex-col space-y-4'>
        {/* Error Message (server 400 detail or client failure) */}
        {errorMessage && (
          <div
            className='bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'
            data-testid='fragment-editor-error'
          >
            {errorMessage}
          </div>
        )}

        {/* Name */}
        <div>
          <label
            htmlFor='fragmentName'
            className='block text-sm font-medium text-foreground dark:text-foreground-dark mb-2'
          >
            Name *
          </label>
          <input
            id='fragmentName'
            type='text'
            value={name}
            disabled={isEditing}
            onChange={(e) => {
              setName(e.target.value);
              if (errorMessage) setErrorMessage(null);
            }}
            placeholder='e.g. top_vendors_by_spend'
            className='w-full px-3 py-2 border border-input rounded-lg font-mono text-sm focus:outline-none dark:text-foreground-dark focus:ring-2 focus:ring-ring focus:border-transparent disabled:opacity-60'
            autoFocus={!isEditing}
          />
          {isEditing ? (
            <div className='text-xs text-muted-foreground mt-1'>
              The name is the function's identity; create a new function to
              rename.
            </div>
          ) : (
            nameError && (
              <div
                className='text-xs text-red-600 mt-1'
                data-testid='fragment-editor-name-error'
              >
                {nameError}
              </div>
            )
          )}
        </div>

        {/* Doc */}
        <div>
          <label
            htmlFor='fragmentDoc'
            className='block text-sm font-medium mb-2 dark:text-foreground-dark'
          >
            Description
          </label>
          <textarea
            id='fragmentDoc'
            value={doc}
            onChange={(e) => setDoc(e.target.value)}
            placeholder='What this function does, for the agent and teammates...'
            rows={2}
            className='w-full px-3 py-2 border border-input rounded-lg text-foreground dark:text-foreground-dark placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none'
          />
        </div>

        {/* Steps JSON */}
        <div className='flex-1 overflow-hidden flex flex-col'>
          <label
            htmlFor='fragmentSteps'
            className='block text-sm font-medium text-foreground mb-2 dark:text-foreground-dark'
          >
            Steps (JSON array) *
          </label>
          <textarea
            id='fragmentSteps'
            value={stepsText}
            onChange={(e) => {
              setStepsText(e.target.value);
              if (errorMessage) setErrorMessage(null);
            }}
            placeholder={STEPS_PLACEHOLDER}
            className='w-full flex-1 min-h-40 px-4 py-3 bg-background border border-input rounded-lg text-sm font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none leading-relaxed'
            data-testid='fragment-editor-steps'
          />
          {stepsError ? (
            <div
              className='text-xs text-red-600 mt-1'
              data-testid='fragment-editor-steps-error'
            >
              {stepsError}
            </div>
          ) : (
            <div
              className='text-xs text-muted-foreground mt-1 leading-relaxed'
              data-testid='fragment-editor-steps-hint'
            >
              A step that omits <code>from</code> consumes the previous
              step&apos;s output. <code>group_table</code> and{' '}
              <code>compare</code> are seed-only — they start from their
              own <code>label</code> and reject a <code>from</code>.
            </div>
          )}
        </div>

        {/* Params (derived) + required subset */}
        <div>
          <div className='text-sm font-medium mb-2 dark:text-foreground-dark'>
            Params{' '}
            <span className='text-xs font-normal text-muted-foreground'>
              (derived from {'{"$param": ...}'} refs; click to toggle
              required)
            </span>
          </div>
          {paramNames.length > 0 ? (
            <div
              className='flex flex-wrap gap-1.5'
              data-testid='fragment-editor-params'
            >
              {paramNames.map((param) => {
                const isRequired = required.includes(param);
                return (
                  <button
                    key={param}
                    type='button'
                    onClick={() => toggleRequired(param)}
                    className={`px-2 py-1 rounded-full text-xs font-mono border transition-colors ${
                      isRequired
                        ? 'bg-primary/10 border-[hsl(var(--primary)/0.4)]'
                        : 'bg-muted border-border hover:bg-muted/70'
                    }`}
                    title={
                      isRequired
                        ? `${param} is required`
                        : `${param} is optional`
                    }
                  >
                    {param}
                    {isRequired ? ' *' : ''}
                  </button>
                );
              })}
            </div>
          ) : (
            <div
              className='text-xs text-muted-foreground'
              data-testid='fragment-editor-params'
            >
              {stepsParse.ok
                ? 'No params referenced.'
                : 'Params appear here once the steps JSON is valid.'}
            </div>
          )}
        </div>

        {/* Output */}
        <div>
          <label
            htmlFor='fragmentOutput'
            className='block text-sm font-medium mb-2 dark:text-foreground-dark'
          >
            Output{' '}
            <span className='text-xs font-normal text-muted-foreground'>
              (optional result alias)
            </span>
          </label>
          <input
            id='fragmentOutput'
            type='text'
            value={output}
            onChange={(e) => setOutput(e.target.value)}
            placeholder='Leave empty to return the last step'
            className='w-full px-3 py-2 border border-input rounded-lg font-mono text-sm focus:outline-none dark:text-foreground-dark focus:ring-2 focus:ring-ring focus:border-transparent'
          />
        </div>
      </div>
    </Dialog>
  );
};

export default FragmentEditorModal;
